import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { CONTENT } from '../content';
import { useTokenomics, TokenomicsData } from '../useTokenomics';
import { ComicReader } from '../comic/ComicReader';
import {
  MEMES, COMIC_BOOK, VIDEOS, TITLES, ROADMAP, ContractAddress, Buy,
  Socials, NAV, UTILITIES, TOKEN_FACTS, ORIGINAL_ART, NDHAUS_PHOTO,
  C, RedesignFonts, Lettering, EASE_OUT, InkRing, Sparkle
} from '../kit';

const WebtoonContainer = styled.div`
  background-color: #0b1528;
  color: ${C.ink};
  min-height: 100dvh;
  width: 100%;
  overflow-x: clip;
  font-family: 'Ubuntu', sans-serif;
  padding-bottom: env(safe-area-inset-bottom);
  * {
    -webkit-tap-highlight-color: transparent;
  }
`;

const HeaderNav = styled.nav`
  position: absolute;
  top: env(safe-area-inset-top);
  left: 0; right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  z-index: 100;
  pointer-events: none;
  & > * { pointer-events: auto; }
`;

const BurgerBtn = styled.button`
  background: ${C.yellow};
  border: 3px solid ${C.ink};
  border-radius: 999px;
  font-family: 'GROBOLD', sans-serif;
  font-size: 16px;
  padding: 8px 16px;
  color: ${C.ink};
  box-shadow: 2px 3px 0 ${C.ink};
  &:active {
    transform: translate(2px, 2px);
    box-shadow: 0 0 0 ${C.ink};
  }
`;

const MobileMenu = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(11, 21, 40, 0.95);
  z-index: 200;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  transition: opacity 300ms ${EASE_OUT};
  
  ul {
    list-style: none;
    padding: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  
  a, button {
    font-family: 'GROBOLD', sans-serif;
    font-size: 32px;
    color: ${C.white};
    text-decoration: none;
    background: none;
    border: none;
    -webkit-text-stroke: 2px ${C.ink};
    paint-order: stroke fill;
  }
  
  .close {
    position: absolute;
    top: calc(env(safe-area-inset-top) + 16px);
    right: 16px;
    background: ${C.pink};
    color: ${C.ink};
    border: 3px solid ${C.ink};
    border-radius: 999px;
    font-size: 20px;
    padding: 8px 16px;
    -webkit-text-stroke: 0;
    box-shadow: 2px 3px 0 ${C.ink};
  }
`;

const comicBorder = css`
  border: 4px solid ${C.ink};
  border-radius: 6px 18px 4px 12px / 14px 4px 18px 6px;
`;
const comicBorderStyle: React.CSSProperties = {
  border: `4px solid ${C.ink}`,
  borderRadius: '6px 18px 4px 12px / 14px 4px 18px 6px',
};

// Speech Bubble with SVG Tail
const Bubble = styled.div<{ $t?: number; $l?: number; $r?: number; $b?: number; $tilt?: number; $tail?: 'bl' | 'br' | 'tl' | 'tr' }>`
  position: absolute;
  top: ${p => p.$t !== undefined ? p.$t + '%' : 'auto'};
  bottom: ${p => p.$b !== undefined ? p.$b + '%' : 'auto'};
  left: ${p => p.$l !== undefined ? p.$l + '%' : 'auto'};
  right: ${p => p.$r !== undefined ? p.$r + '%' : 'auto'};
  background: ${C.white};
  border: 3px solid ${C.ink};
  border-radius: 20px;
  padding: 12px 16px;
  font-size: 16px;
  line-height: 1.35;
  font-weight: 500;
  box-shadow: 3px 4px 0 ${C.ink};
  transform: rotate(${p => p.$tilt || 0}deg);
  z-index: 10;
  max-width: 75%;

  svg.tail {
    position: absolute;
    width: 28px;
    height: 28px;
    fill: ${C.white};
    stroke: ${C.ink};
    stroke-width: 3px;
    z-index: -1;

    ${p => p.$tail === 'bl' && 'bottom: -20px; left: 20px; transform: rotate(0deg);'}
    ${p => p.$tail === 'br' && 'bottom: -20px; right: 20px; transform: scaleX(-1);'}
    ${p => p.$tail === 'tl' && 'top: -20px; left: 20px; transform: scaleY(-1);'}
    ${p => p.$tail === 'tr' && 'top: -20px; right: 20px; transform: scale(-1, -1);'}
  }
`;

const BubbleTail = ({ type }: { type: 'bl' | 'br' | 'tl' | 'tr' }) => (
  <svg className="tail" viewBox="0 0 40 40">
    <path d="M0,0 C10,20 20,35 40,40 C25,25 20,10 20,0 Z" />
  </svg>
);

const Caption = styled.div<{ $bg?: string; $tilt?: number }>`
  background: ${p => p.$bg || C.yellow};
  ${comicBorder}
  padding: 8px 14px;
  font-family: 'BreakingTheComic', sans-serif;
  font-size: 22px;
  text-transform: uppercase;
  box-shadow: 4px 5px 0 ${C.ink};
  display: inline-block;
  transform: rotate(${p => p.$tilt || -2}deg);
  z-index: 5;
`;

const HeroSplash = styled.section`
  position: relative;
  width: 100%;
  height: 100dvh;
  overflow: hidden;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;

  .bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 70% center;
    opacity: 0.8;
  }
  
  .content {
    position: relative;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 18vh;
    width: 100%;
  }

  .title-zeus {
    width: 85%;
    transform: rotate(-3deg);
    filter: drop-shadow(0 4px 12px rgba(0,0,0,0.5));
  }
  .title-dog {
    width: 60%;
    transform: rotate(2deg);
    margin-top: -10px;
    filter: drop-shadow(0 4px 12px rgba(0,0,0,0.5));
  }

  .actions {
    position: absolute;
    bottom: 8vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    z-index: 10;
  }
`;

const ComicPage = styled.div`
  padding: 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RevealPanel = styled.div<{ $tilt?: number; $w?: string; $align?: 'flex-start' | 'flex-end' | 'center' }>`
  position: relative;
  width: ${p => p.$w || '100%'};
  align-self: ${p => p.$align || 'center'};
  transform: translateY(30px) rotate(${p => p.$tilt || 0}deg);
  opacity: 0;
  transition: opacity 600ms ${EASE_OUT}, transform 600ms ${EASE_OUT};
  &.visible {
    opacity: 1;
    transform: translateY(0) rotate(${p => p.$tilt || 0}deg);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    opacity: 1;
    transform: translateY(0) rotate(${p => p.$tilt || 0}deg);
  }
`;

const PanelImage = styled.div<{ $src: string; $aspect?: number; $border?: boolean }>`
  width: 100%;
  aspect-ratio: ${p => p.$aspect || 1};
  background-image: url(${p => p.$src});
  background-size: cover;
  background-position: center;
  ${p => p.$border !== false && comicBorder}
`;

const VideoPanel = styled.video`
  width: 100%;
  ${comicBorder}
  display: block;
`;

const BreakoutArt = styled.img<{ $w?: string; $t?: string; $l?: string; $r?: string; $b?: string; $tilt?: number }>`
  position: absolute;
  width: ${p => p.$w || 'auto'};
  top: ${p => p.$t || 'auto'};
  left: ${p => p.$l || 'auto'};
  right: ${p => p.$r || 'auto'};
  bottom: ${p => p.$b || 'auto'};
  transform: rotate(${p => p.$tilt || 0}deg);
  z-index: 20;
  pointer-events: none;
`;

const FactPanel = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 12px;
  background: ${C.white};
  ${comicBorder}
  text-decoration: none;
  color: ${C.ink};
  position: relative;
  margin-top: 24px;
  
  img {
    width: 140px;
    margin-top: -40px;
    transform: rotate(-3deg);
    transition: transform 300ms ${EASE_OUT};
  }
  
  .val {
    margin-top: -10px;
  }
  
  .lbl {
    font-family: 'GROBOLD', sans-serif;
    font-size: 20px;
    margin-top: 8px;
    text-transform: uppercase;
  }
  
  .desc {
    font-size: 15px;
    margin-top: 6px;
    line-height: 1.3;
  }
`;

const TokenomicsPie: React.FC<{ data: TokenomicsData | null; failed: boolean }> = ({ data, failed }) => {
  const lpDeg = data ? (data.lpZeusPct / 100) * 360 : 0;
  const span = Math.max(lpDeg, 4);
  const a0 = -90 * Math.PI / 180;
  const a1 = (span - 90) * Math.PI / 180;
  const large = span > 180 ? 1 : 0;
  const path = `M100 100 L${100 + 90 * Math.cos(a0)} ${100 + 90 * Math.sin(a0)} A90 90 0 ${large} 1 ${100 + 90 * Math.cos(a1)} ${100 + 90 * Math.sin(a1)} Z`;

  return (
    <RevealPanel $w="100%" $tilt={1}>
      <div style={{ background: C.white, padding: '24px 16px', ...comicBorderStyle, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Caption $bg={C.white} $tilt={-1} style={{ marginBottom: 16 }}>WHERE'S THE SUPPLY?</Caption>
        <div style={{ position: 'relative', width: 220, height: 220 }}>
          <svg viewBox="0 0 200 200" width="100%" height="100%">
            <circle cx="100" cy="100" r="90" fill={data ? C.paper : '#dde6f2'} stroke={C.ink} strokeWidth="6" />
            {data && <path d={path} fill={C.yellow} stroke={C.ink} strokeWidth="6" strokeLinejoin="round" />}
            <circle cx="100" cy="100" r="10" fill={C.ink} />
          </svg>
          
          <Bubble $t={-10} $r={-20} $tilt={2} $tail="bl" style={{ width: 140 }}>
            <strong>LP Locked</strong><br/>
            {data ? data.lpZeusPct.toFixed(1) + '%' : '...'}
            <BubbleTail type="bl" />
          </Bubble>
          
          <Bubble $b={-15} $l={-10} $tilt={-2} $tail="tr" style={{ width: 140 }}>
            <strong>Circulating</strong><br/>
            {data ? data.circulatingPct.toFixed(1) + '%' : '...'}
            <BubbleTail type="tr" />
          </Bubble>
        </div>
      </div>
    </RevealPanel>
  );
};

const useReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

export default function MobileWebtoon() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { data, failed } = useTokenomics();
  const [comicOpen, setComicOpen] = useState(false);
  useReveal();

  return (
    <WebtoonContainer>
      <RedesignFonts />
      <HeaderNav>
        <img src="/assets/img/logo.webp" alt="Logo" style={{ width: 44, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
        <BurgerBtn onClick={() => setMenuOpen(true)}>MENU</BurgerBtn>
      </HeaderNav>

      <MobileMenu $open={menuOpen}>
        <button className="close" onClick={() => setMenuOpen(false)}>X</button>
        <ul>
          {NAV.map(n => (
            <li key={n.label}><a href={n.href} onClick={() => setMenuOpen(false)}>{n.label}</a></li>
          ))}
          <li><a href="#dollarize" onClick={() => setMenuOpen(false)}>DOLLARIZE</a></li>
          <li><a href="#roadmap" onClick={() => setMenuOpen(false)}>ROADMAP</a></li>
          {UTILITIES.map(u => (
            <li key={u.label}><a href={u.href} target="_blank" rel="noreferrer">{u.label}</a></li>
          ))}
        </ul>
      </MobileMenu>

      <HeroSplash>
        <img className="bg" src="/redesign2/scenes/camp.webp" alt="" />
        <div className="content">
          <img className="title-zeus" src={TITLES.zeus.src} alt={TITLES.zeus.alt} />
          <img className="title-dog" src={TITLES.pepesDog.src} alt={TITLES.pepesDog.alt} />
        </div>
        
        <Bubble $t={38} $l={10} $tilt={-3} $tail="br" style={{ width: 220 }}>
          {CONTENT.about.paragraphs[0]}
          <BubbleTail type="br" />
        </Bubble>

        <div className="actions">
          <ContractAddress tone="light" />
          <Buy big />
          <div style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }}>
            <Socials size={36} color={C.white} />
          </div>
        </div>
      </HeroSplash>

      <ComicPage>
        <RevealPanel className="reveal" $w="90%" $tilt={1}>
          <PanelImage $src={VIDEOS.trailer.poster} $aspect={1.2}>
            <VideoPanel src={VIDEOS.trailer.src} poster={VIDEOS.trailer.poster} controls playsInline style={{ position: 'absolute', inset: 0, height: '100%', objectFit: 'cover' }} />
          </PanelImage>
          <Caption style={{ position: 'absolute', top: -16, left: 10 }}>THE STORY</Caption>
        </RevealPanel>

        <RevealPanel className="reveal" $w="85%" $align="flex-end" $tilt={-1}>
          <div style={{ background: C.paper, padding: 16, ...comicBorderStyle }}>
            <p style={{ margin: '0 0 12px 0', fontSize: 16, lineHeight: 1.4, fontWeight: 500 }}>
              {CONTENT.about.paragraphs[1]} {CONTENT.about.paragraphs[2]}
            </p>
            <p style={{ margin: 0, fontSize: 16, lineHeight: 1.4, fontWeight: 500 }}>
              {CONTENT.about.paragraphs[3]} <br/>
              <a href={CONTENT.about.trademarkLink} target="_blank" rel="noreferrer" style={{ color: C.pink, fontWeight: 'bold' }}>View Trademark</a>
            </p>
          </div>
          <BreakoutArt src={TITLES.takeOver.src} $w="120%" $t="-40px" $r="-10px" $tilt={3} />
        </RevealPanel>

        <RevealPanel className="reveal" $w="100%" $tilt={0} style={{ marginTop: 24 }}>
          <PanelImage $src="/redesign2/scenes/pool.webp" $aspect={1.4} />
          <Bubble $t={10} $l={5} $tilt={-2} $tail="bl">
            {CONTENT.aboutCto.paragraphs[0]}
            <BubbleTail type="bl" />
          </Bubble>
          <Bubble $b={10} $r={5} $tilt={2} $tail="br">
            {CONTENT.aboutCto.paragraphs[1]}
            <BubbleTail type="br" />
          </Bubble>
        </RevealPanel>

        <RevealPanel className="reveal" $w="90%" $tilt={1}>
          <VideoPanel src={VIDEOS.buy.src} poster={VIDEOS.buy.poster} controls playsInline />
          <Caption style={{ position: 'absolute', bottom: -16, right: 10 }}>CTO</Caption>
        </RevealPanel>
      </ComicPage>

      <ComicPage id="tokenomics">
        <BreakoutArt src={TITLES.tokenomics.src} $w="90%" $t="0" $l="5%" $tilt={-2} style={{ position: 'relative', margin: '20px 0' }} />
        
        <TokenomicsPie data={data} failed={failed} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 32 }}>
          {TOKEN_FACTS.map((f, i) => (
            <RevealPanel key={i} className="reveal" $tilt={i % 2 === 0 ? -1 : 2}>
              <FactPanel href={f.link} target="_blank" rel="noreferrer">
                <img src={'/redesign2/tokenomics/' + (f.title === 'cONTRACT' ? 'locked' : f.title === 'Tax' ? 'tax' : f.title === 'LP Renounced' ? 'burned' : 'supply') + '.webp'} alt="" />
                <div className="val">
                  <Lettering $size="26px">{f.text.includes('420') ? '420.69T' : f.title === 'LP Renounced' ? 'BURNED' : f.title === 'cONTRACT' ? 'LOCKED' : f.text}</Lettering>
                </div>
                <div className="lbl">{f.title}</div>
                <div className="desc">{f.title === 'LP Renounced' ? 'Liquidity locked forever' : f.title === 'Tax' ? 'No tax on buys or sells' : ''}</div>
              </FactPanel>
            </RevealPanel>
          ))}
        </div>
      </ComicPage>

      <ComicPage id="dollarize">
        <RevealPanel className="reveal" $w="100%">
          <PanelImage $src="/redesign2/scenes/blackhole.webp" $aspect={1} />
          <Caption style={{ position: 'absolute', top: -16, left: 10 }}>{CONTENT.dollarize.title}</Caption>
          <Bubble $t={15} $r={5} $tilt={1} $tail="br">
            {CONTENT.dollarize.paragraphs[0]}
            <BubbleTail type="br" />
          </Bubble>
          <Bubble $b={15} $l={5} $tilt={-1} $tail="tl">
            {CONTENT.dollarize.paragraphs[2]}
            <BubbleTail type="tl" />
          </Bubble>
        </RevealPanel>

        <RevealPanel className="reveal" $w="90%" $align="flex-end" $tilt={-1}>
          <div style={{ background: C.white, padding: 20, ...comicBorderStyle, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <a href={CONTENT.dollarize.mintLink} target="_blank" rel="noreferrer" style={{ background: C.yellow, border: '3px solid ' + C.ink, padding: '14px', fontWeight: 'bold', textDecoration: 'none', color: C.ink, borderRadius: 999, textAlign: 'center', fontSize: 18 }}>MINT USDZEUS</a>
            <a href={CONTENT.dollarize.howLink} target="_blank" rel="noreferrer" style={{ background: C.mist, border: '3px solid ' + C.ink, padding: '14px', fontWeight: 'bold', textDecoration: 'none', color: C.ink, borderRadius: 999, textAlign: 'center', fontSize: 18 }}>HOW IT WORKS</a>
          </div>
        </RevealPanel>
      </ComicPage>

      <ComicPage id="roadmap">
        <BreakoutArt src={TITLES.roadmap.src} $w="80%" $t="0" $l="10%" $tilt={1} style={{ position: 'relative', margin: '20px 0' }} />
        
        <div style={{ position: 'relative', paddingLeft: 16 }}>
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: 24, width: 4, background: C.ink, borderRadius: 4 }} />
          
          {ROADMAP.map((r, i) => (
            <RevealPanel key={i} className="reveal" $w="calc(100% - 24px)" $align="flex-end" $tilt={i % 2 === 0 ? 1 : -1} style={{ marginBottom: 24 }}>
              <div style={{ position: 'absolute', left: -26, top: 20, width: 16, height: 16, background: r.milestone.status === 'shipped' ? C.green : C.yellow, border: '3px solid ' + C.ink, borderRadius: '50%', zIndex: 5 }} />
              <div style={{ background: C.white, padding: 16, ...comicBorderStyle }}>
                <Lettering $size="22px">{r.milestone.title}</Lettering>
                <div style={{ fontWeight: 'bold', margin: '8px 0', color: r.milestone.status === 'shipped' ? C.green : C.orange }}>{r.milestone.status.toUpperCase()}</div>
                <p style={{ margin: 0, fontSize: 15, lineHeight: 1.4, fontWeight: 500 }}>{r.milestone.description}</p>
              </div>
            </RevealPanel>
          ))}
        </div>
      </ComicPage>

      <ComicPage>
        <BreakoutArt src={TITLES.artist.src} $w="70%" $t="0" $l="15%" $tilt={-1} style={{ position: 'relative', margin: '10px 0 20px' }} />
        
        <RevealPanel className="reveal" $w="100%" $tilt={1}>
          <PanelImage $src={NDHAUS_PHOTO} $aspect={1} />
          <Bubble $t={70} $l={5} $tilt={-2} $tail="tl">
            {CONTENT.artist.paragraphs[0]}
            <BubbleTail type="tl" />
          </Bubble>
        </RevealPanel>

        <RevealPanel className="reveal" $w="95%" $align="flex-end" $tilt={-1}>
          <PanelImage $src={ORIGINAL_ART} $aspect={1.2} />
          <Bubble $b={-15} $r={10} $tilt={2} $tail="tr">
            {CONTENT.artist.paragraphs[2]}
            <BubbleTail type="tr" />
          </Bubble>
        </RevealPanel>
      </ComicPage>

      <ComicPage>
        <RevealPanel className="reveal" $w="100%" $tilt={1}>
          <PanelImage $src="/redesign2/nfts/hero.webp" $aspect={1} />
          <Caption style={{ position: 'absolute', top: -16, right: 10 }}>{CONTENT.nfts.title}</Caption>
          <Bubble $t={10} $l={10} $tilt={-1} $tail="bl">
            {CONTENT.nfts.paragraphs[0]}
            <BubbleTail type="bl" />
          </Bubble>
          <a href={CONTENT.nfts.link} target="_blank" rel="noreferrer" style={{ position: 'absolute', bottom: 16, right: 16, background: C.pink, color: C.white, fontWeight: 'bold', padding: '12px 20px', border: '3px solid ' + C.ink, borderRadius: 999, textDecoration: 'none', zIndex: 20 }}>VIEW ON OPENSEA</a>
        </RevealPanel>
      </ComicPage>

      <ComicPage style={{ paddingBottom: 40 }}>
        <BreakoutArt src={TITLES.memes.src} $w="70%" $t="0" $l="15%" $tilt={2} style={{ position: 'relative', margin: '10px 0 20px' }} />
        
        {MEMES.slice(0, 3).map((m, i) => (
          <RevealPanel key={i} className="reveal" $w={i % 2 === 0 ? "85%" : "95%"} $align={i % 2 === 0 ? "flex-start" : "flex-end"} $tilt={i % 2 === 0 ? -2 : 1}>
            <img src={m.src} alt={m.alt} style={{ width: '100%', ...comicBorderStyle, background: C.white }} />
          </RevealPanel>
        ))}
      </ComicPage>

      <ComicPage>
        <BreakoutArt src={TITLES.comic.src} $w="85%" $t="0" $l="7%" $tilt={-1} style={{ position: 'relative', margin: '10px 0 20px' }} />
        
        <RevealPanel className="reveal" $w="100%" $tilt={1}>
          <div style={{ background: C.paper, padding: 16, ...comicBorderStyle, textAlign: 'center' }}>
            <p style={{ fontWeight: 500, fontSize: 16, margin: '0 0 16px', lineHeight: 1.4 }}>{CONTENT.comic.subtitle}</p>
            <img src={COMIC_BOOK[0].src} alt="Comic Cover" style={{ width: '80%', ...comicBorderStyle, cursor: 'pointer' }} onClick={() => setComicOpen(true)} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24, alignItems: 'center' }}>
              <button onClick={() => setComicOpen(true)} style={{ background: C.yellow, border: '3px solid ' + C.ink, padding: '14px 32px', fontWeight: 'bold', borderRadius: 999, fontSize: 18, color: C.ink }}>READ COMIC</button>
              <a href="/zeus.pdf" download style={{ textDecoration: 'none', color: C.ink, fontWeight: 'bold', fontSize: 16 }}>Download PDF</a>
            </div>
          </div>
        </RevealPanel>
      </ComicPage>

      <ComicPage>
        <RevealPanel className="reveal" $w="100%">
          <PanelImage $src="/redesign2/scenes/gang.webp" $aspect={1.2} />
          <div style={{ position: 'absolute', bottom: -20, left: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, zIndex: 10 }}>
            <Lettering $size="32px">JOIN THE GANG</Lettering>
            <Buy big />
            <div style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }}>
              <Socials size={40} color={C.white} />
            </div>
          </div>
        </RevealPanel>
      </ComicPage>

      <div style={{ padding: '60px 20px 20px', textAlign: 'center', color: C.light }}>
        <img src="/assets/img/logo.webp" alt="Logo" style={{ width: 44, opacity: 0.5 }} />
        <div style={{ marginTop: 12, fontSize: 15, fontWeight: 500 }}>
          <a href="/terms" style={{ color: C.light, marginRight: 20, textDecoration: 'none' }}>Terms</a>
          <a href="/privacy" style={{ color: C.light, textDecoration: 'none' }}>Privacy</a>
        </div>
      </div>

      {comicOpen && (
        <ComicReader pages={COMIC_BOOK} startPage={0} pdf="/zeus.pdf" onClose={() => setComicOpen(false)} />
      )}
    </WebtoonContainer>
  );
}
